import type {
  StrategyFactsheetResponseDTO,
  StrategyAttributesDTO,
  UploadTrackRequestDTO,
  StrategyAttachmentResponseDTO,
} from '@/api-v2/web/strategies';
import {
  getStrategyFactsheet,
  setStrategyAttributes,
  getStrategyIdentifiers,
  uploadStrategy,
  getStrategyAttachment,
  getStrategyFundamental,
} from '@/api-v2/web/strategies';
import type { LoadingWhenEnabledQRT, VQQueryOptions } from '@/types/VueQueryTypes';
import type { QueryObserverOptions, UseMutationReturnType, UseQueryReturnType } from '@tanstack/vue-query';
import { useQuery } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { chainEnabled, unwrap } from '@/utils/queries';
import type { VQMutationOptions } from './defineMutation';
import { defineMutation, useManagedMutation } from './defineMutation';
import type { StrategyIdentifiersResponseDTO } from '@/api-v2/web/strategies/types/StrategyIdentifiers';
import type { StrategyAttachmentType } from '@/types/StrategyAttachment';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';
import type { MaybeRef } from '@vueuse/shared';
import type { StrategyFundamentalResponseDTO } from '@/api-v2/web/strategies/types/StrategyFundamentalResponseDTO';
import type { GetTracksDTO } from '@/api-v2/web/strategies/types/GetTracksDTO';

const keys = {
  all: () => [{ scope: 'strategies' }] as const,
  factsheet: (indexUniqueIdentifier: Ref<string | null | undefined>) =>
    [{ ...keys.all()[0], entity: 'factsheet', indexUniqueIdentifier }] as const,
  identifiers: (query: Ref<string[]>) => [{ ...keys.all()[0], entity: 'identifiers', query }] as const,
  attachment: (code: Ref<string | undefined>, type?: StrategyAttachmentType) =>
    [{ ...keys.all()[0], entity: 'attachment', code, type }] as const,
  liveObjects: () => [{ ...keys.all()[0], entity: 'liveObjects' }] as const,
  fundamental: (query: Ref<GetTracksDTO>) => [{ ...keys.all()[0], entity: 'fundamental', query }] as const,
};

/**
 * Key for discovery page scope.
 *
 * Copied from useDataDiscovery. We need to invalidate some of the cache here.
 */
function discoveryAllKey(): readonly [{ scope: 'discover' }] {
  return [{ scope: 'discover' }] as const;
}

/**
 * Get a map of identifiers for list of strategies.
 */
export function useStrategyIdentifiersByCode(
  query: Ref<string[]>,
  options: VQQueryOptions<Map<string, StrategyIdentifiersResponseDTO>> = {},
): UseQueryReturnType<Map<string, StrategyIdentifiersResponseDTO>, Error> {
  return useQuery<Map<string, StrategyIdentifiersResponseDTO>, Error>({
    queryKey: keys.identifiers(query),
    queryFn: async ({ signal }): Promise<Map<string, StrategyIdentifiersResponseDTO>> => {
      if (query.value.length === 0) {
        throw new Error('Query must not be empty');
      }
      const result = await getStrategyIdentifiers({ codes: query.value }, signal);
      return new Map(result.map((o) => [o.code, o]));
    },
    ...options,
    enabled: chainEnabled(
      options.enabled,
      computed(() => query.value.length > 0),
    ),
  });
}

/**
 * Get strategy factsheet response.
 */
export function useStrategyFactsheet(
  indexUniqueIdentifier: Ref<string | null | undefined>,
  options: { enabled?: MaybeRef<boolean> } & Partial<
    Omit<QueryObserverOptions<StrategyFactsheetResponseDTO, Error>, 'enabled'>
  > = {},
): LoadingWhenEnabledQRT<StrategyFactsheetResponseDTO, Error> {
  const enabled = chainEnabled(
    options.enabled,
    computed(() => indexUniqueIdentifier.value != null),
  );

  return useLoadingWhenEnabledQuery(
    useQuery<StrategyFactsheetResponseDTO, Error>({
      queryKey: keys.factsheet(indexUniqueIdentifier),
      queryFn: ({ signal }): Promise<StrategyFactsheetResponseDTO> =>
        getStrategyFactsheet(unwrap(indexUniqueIdentifier), signal),
      ...options,
      enabled,
    }),
    enabled,
  );
}

/**
 * Set strategy attributes
 */
export function useSetStrategyAttributes(
  code: Ref<string | undefined>,
  options: VQMutationOptions<void, Error, StrategyAttributesDTO> = {},
): UseMutationReturnType<void, Error, StrategyAttributesDTO, unknown> {
  return useManagedMutation(
    {
      mutationFn: (input: StrategyAttributesDTO) => {
        if (!code.value) throw new Error('Code is required');

        return setStrategyAttributes(code.value, input);
      },
      invalidateCache(client) {
        return client.invalidateQueries({ queryKey: keys.factsheet(code) });
      },
    },
    options,
  );
}

/**
 * Upload private track
 */
export const useUploadStrategy = defineMutation({
  mutationFn: (body: UploadTrackRequestDTO) => uploadStrategy(body),
  invalidateCache(client) {
    return client.invalidateQueries({ queryKey: discoveryAllKey() });
  },
});

export function useStrategyAttachment(
  code: Ref<string | undefined>,
  type: StrategyAttachmentType,
  options: VQQueryOptions<StrategyAttachmentResponseDTO | null> = {},
): UseQueryReturnType<StrategyAttachmentResponseDTO | null, Error> {
  return useQuery<StrategyAttachmentResponseDTO | null, Error>({
    ...options,
    enabled: chainEnabled(
      options.enabled,
      computed(() => code.value != null),
    ),
    queryKey: keys.attachment(code, type),
    queryFn: ({ signal }): Promise<StrategyAttachmentResponseDTO | null> =>
      getStrategyAttachment(unwrap(code), signal, type),
  });
}

export function useStrategyFundamentalTracks(
  query: Ref<GetTracksDTO>,
  options: VQQueryOptions<StrategyFundamentalResponseDTO | null> = {},
): LoadingWhenEnabledQRT<StrategyFundamentalResponseDTO | null, Error> {
  const enabled = chainEnabled(
    options.enabled,
    computed(() => !!query.value && query.value.analysisCodes.length > 0),
  );
  return useLoadingWhenEnabledQuery(
    useQuery<StrategyFundamentalResponseDTO | null, Error>({
      queryKey: keys.fundamental(query),
      queryFn: ({ signal }): Promise<StrategyFundamentalResponseDTO | null> =>
        getStrategyFundamental(unwrap(query), signal),
      ...options,
      enabled,
    }),
    enabled,
  );
}
